<template>
   <div>
      <!-- Native xsurflog Header -->
      <div class="row p-2 mt-4">
         <div class="col-12">
            <span class="custom-span-lg" style="color: #435666;">Configuration
               <i class="fa-solid fa-arrow-right"></i>
            </span>
            <span class="custom-span-no-weight" style="color: #435666;"> &nbsp;Domain List</span>
            <div class="mt-2">
               <small class="text-muted">
                  <i class="fa-solid fa-circle-info me-1" style="color: #00A0C1;"></i>
                  Consolidate your whitelist and blocklist into a singular table. Add root domains, set default actions, configure granular subdomain or path override exceptions, and manage DLP or file upload permissions.
               </small>
            </div>
         </div>
      </div>

      <!-- Main Native Card -->
      <div class="card mt-3">
         <div class="card-body m-2">
            <!-- Top Action Bar: Add Domain / Exception Pattern -->
            <div class="row p-2 mb-4 align-items-end" style="row-gap: 15px;">
               <div class="col-12 col-md-4 col-lg-3">
                  <label class="form-label text-dark small fw-bold mb-1">Domain or Exception URL</label>
                  <input v-model="newDomain.domain" type="text" class="form-control form-control-sm" 
                     placeholder="e.g. google.com, api.google.com/v1/*" required style="border-radius: 8px; height: 40px; font-size: 14px;" />
               </div>
               <div class="col-12 col-md-3 col-lg-3">
                  <label class="form-label text-dark small fw-bold mb-1">Audit Justification Note</label>
                  <input v-model="newDomain.note" type="text" class="form-control form-control-sm" 
                     placeholder="Compliance or auditing note" style="border-radius: 8px; height: 40px; font-size: 14px;" />
               </div>
               <div class="col-12 col-md-2 col-lg-2">
                  <label class="form-label text-dark small fw-bold mb-1">Action</label>
                  <select v-model="newDomain.default_action" class="form-select shadow-none bordered-select" style="border-radius: 8px; height: 40px; font-size: 14px;">
                     <option value="allow">Allow</option>
                     <option value="block">Block</option>
                  </select>
               </div>
               <div class="col-12 col-md-1 col-lg-2">
                  <div class="form-check form-switch mb-2 h-100 d-flex align-items-center gap-2">
                     <input v-model="newDomain.apply_to_subdomains" class="form-check-input" type="checkbox" id="subdomainCheck" style="cursor: pointer; width: 40px; height: 20px;">
                     <label class="form-check-label text-dark small fw-bold mb-0" for="subdomainCheck" style="cursor: pointer; user-select: none;">
                        Inherit
                     </label>
                  </div>
               </div>
               <div class="col-12 col-md-2 col-lg-2">
                  <button type="button" @click.prevent="addDomain()" class="fw-bold btn btn-info text-light w-100 d-flex align-items-center justify-content-center gap-1" style="font-size: 14px; height: 40px; border-radius: 8px !important; white-space: nowrap;">
                     <i class="fa-solid fa-plus"></i> Add Domain
                  </button>
               </div>
            </div>

            <!-- Search + Filters Bar -->
            <div class="row p-2 align-items-center mb-3">
               <div class="col-12 col-md-4 mb-2 mb-md-0">
                  <input v-model="search" type="text" class="form-control form-control-sm" 
                     placeholder="&#xF002; Search domains, patterns or notes..."
                     style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                     @keyup="onSearch()" />
               </div>

               <div class="col-12 col-md d-flex flex-wrap justify-content-md-end align-items-center">
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Rows per page:</span>
                     <select v-model="pagination.pageSize" class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;" @change="getDomainList()">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                     </select>
                  </div>

                  <div class="pagination" v-if="pagination.totalItems > 0">
                     <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow">
                        <i class="fa fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ (pagination.page - 1) * pagination.pageSize + 1 }}-{{ Math.min(pagination.page * pagination.pageSize, pagination.totalItems) }}</strong> of {{ pagination.totalItems }}
                     </span>
                     <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages" class="pagination-arrow">
                        <i class="fa fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <!-- Main Table -->
            <div class="row p-2">
               <div class="table-responsive border rounded-3" style="background-color: white; overflow: hidden; width: 100%;">
                  <table class="table align-middle mainTable mb-0" style="font-size: 14px;">
                     <thead>
                        <tr>
                           <th scope="col" style="width: 40px;"></th>
                           <th scope="col">Base Domain</th>
                           <th scope="col" style="width: 180px;" class="text-center">Default Action</th>
                           <th scope="col" style="width: 180px;" class="text-center">Subdomains Inherit</th>
                           <th scope="col" style="width: 250px;">Audit Justification Note</th>
                           <th scope="col" style="width: 180px;" class="text-center">Exceptions</th>
                           <th scope="col" style="width: 120px;" class="text-end pe-3">Actions</th>
                        </tr>
                     </thead>

                     <tbody v-if="isLoading">
                        <tr>
                           <td colspan="7" class="text-center py-5">
                              <div class="spinner-border text-info spinner-border-sm me-2" role="status"></div>
                              <span class="text-muted">Loading domain records...</span>
                           </td>
                        </tr>
                     </tbody>

                     <tbody v-else-if="!domains || domains.length === 0">
                        <tr>
                           <td colspan="7" class="text-center py-5 text-muted">
                              <i class="fa-regular fa-folder-open d-block fs-2 mb-2 text-secondary"></i>
                              No registered domains found. Add a domain above to start.
                           </td>
                        </tr>
                     </tbody>

                     <tbody v-else>
                        <template v-for="(domain, idx) in domains" :key="domain.id">
                           <!-- Parent Row -->
                           <tr class="align-middle border-bottom" :class="{ 'bg-light-yellow': expandedRowIndex === idx }" style="cursor: pointer; transition: background 0.15s;" @click="toggleRow(idx)">
                              <td class="text-center ps-3" style="padding: 15px 10px;">
                                 <i class="fa-solid" :class="expandedRowIndex === idx ? 'fa-chevron-down text-info' : 'fa-chevron-right text-muted'" style="font-size: 12px; transition: transform 0.2s;"></i>
                              </td>
                              <td style="padding: 15px 20px;">
                                 <span class="fw-bold text-dark d-flex align-items-center gap-2" style="font-size: 15px;">
                                    <i class="fa-solid fa-globe" style="font-size: 14px; color: #00A0C1;"></i>
                                    {{ domain.domain }}
                                 </span>
                              </td>
                              <td style="padding: 15px 20px;" class="text-center" @click.stop>
                                 <div class="btn-group" role="group">
                                    <button type="button"
                                       class="btn btn-outline-left btn-sm rounded-start-pill btn-check-left hand-cursor px-3"
                                       @click="updateDefaultAction(domain, 'allow')"
                                       :class="{ active: domain.default_action === 'allow' }"
                                       style="font-size: 11px; font-weight: bold; height: 30px;">Allow</button>
                                    <button type="button"
                                       class="btn btn-outline-right btn-sm rounded-end-pill btn-check-right hand-cursor px-3"
                                       @click="updateDefaultAction(domain, 'block')"
                                       :class="{ active: domain.default_action === 'block' }"
                                       style="font-size: 11px; font-weight: bold; height: 30px;">Block</button>
                                 </div>
                              </td>
                              <td style="padding: 15px 20px;" class="text-center">
                                 <div class="form-check form-switch d-flex justify-content-center m-0" @click.stop>
                                    <input class="form-check-input" type="checkbox" role="switch" :checked="domain.apply_to_subdomains > 0"
                                       @change="toggleSubdomainInherit(domain)" style="cursor: pointer; width: 34px; height: 17px;" />
                                 </div>
                              </td>
                              <!-- Base Domain Audit Note Inline -->
                              <td @click.stop style="padding: 15px 20px;">
                                 <input v-model="domain.note" type="text" 
                                    class="form-control form-control-xs font-monospace bg-light border-0" 
                                    placeholder="No note added" 
                                    @change="saveDomainNote(domain)"
                                    style="font-size: 12px; height: 28px; border-radius: 4px; padding: 2px 6px; width: 100%; max-width: 230px;" />
                              </td>
                              <td style="padding: 15px 20px;" class="text-center">
                                 <span class="badge bg-secondary-subtle text-dark border px-3 py-2" style="border-radius: 6px; font-weight: 500; font-size: 12px;">
                                    <i class="fa-solid fa-layer-group me-1 text-secondary"></i>
                                    {{ domain.rules ? domain.rules.length : 0 }} Exceptions
                                 </span>
                              </td>
                              <td style="padding: 15px 20px;" class="text-end pe-3" @click.stop>
                                 <div class="d-flex justify-content-end gap-2">
                                    <button class="btn btn-sm btn-outline-primary" @click="toggleRow(idx)" title="View Exceptions" style="border-radius: 6px;">
                                       <i class="fa-solid fa-list-check"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-danger" @click="deleteDomain(domain)" title="Delete Domain" style="border-radius: 6px;">
                                       <i class="fa-solid fa-trash-can"></i>
                                    </button>
                                 </div>
                              </td>
                           </tr>

                           <!-- Nested Accordion Rule Exception Section (Flat Priority List) -->
                           <tr v-if="expandedRowIndex === idx">
                              <td colspan="7" class="p-0" style="background-color: #fcfdfe !important;">
                                 <div class="p-4 border-bottom">
                                    <div class="row align-items-center mb-3">
                                       <div class="col">
                                          <h6 class="m-0 fw-bold text-dark d-flex align-items-center gap-2" style="font-size: 14px; color: #435666 !important;">
                                             <i class="fa-solid fa-code-branch" style="color: #00A0C1;"></i>
                                             Exceptions & Overrides for <span class="text-info fw-bold">{{ domain.domain }}</span>
                                          </h6>
                                          <p class="text-muted mb-0 small">Define ordered exception rules evaluated from top to bottom (highest priority first).</p>
                                       </div>
                                    </div>

                                    <!-- Flat Exception Rules Table -->
                                    <div v-if="!domain.rules || domain.rules.length === 0" class="alert alert-light border text-center text-muted mb-3 py-3" style="border-radius: 8px;">
                                       <i class="fa-solid fa-circle-info me-1" style="color: #00A0C1;"></i> No exception rules configured for this root domain. Add one below.
                                    </div>

                                    <div v-else class="table-responsive border rounded mb-3 bg-white" style="overflow: hidden; width: 100%;">
                                       <table class="table table-sm table-hover align-middle mb-0" style="font-size: 13px;">
                                          <thead class="table-light text-secondary uppercase text-xs" style="letter-spacing: 0.3px;">
                                             <tr>
                                                <th scope="col" class="ps-3 py-2.5" style="width: 100px;">Priority</th>
                                                <th scope="col" class="py-2.5">Rule Pattern</th>
                                                <th scope="col" class="py-2.5" style="width: 120px;">Subdomains</th>
                                                <th scope="col" class="py-2.5" style="width: 110px;">File Upload</th>
                                                <th scope="col" class="py-2.5" style="width: 250px;">Audit Note / Reason</th>
                                                <th scope="col" class="py-2.5" style="width: 140px;">Action</th>
                                                <th scope="col" class="text-end pe-3 py-2.5" style="width: 70px;">Remove</th>
                                             </tr>
                                          </thead>
                                          <tbody>
                                             <tr v-for="(rule, ruleIdx) in domain.rules" :key="'rule-' + ruleIdx" class="align-middle"
                                                :class="{ 'dragging-row': draggingDomainId === domain.id && draggingRuleIndex === ruleIdx }"
                                                draggable="true"
                                                @dragstart="onDragStart($event, domain, ruleIdx)"
                                                @dragover.prevent
                                                @dragenter.prevent
                                                @drop="onDrop($event, domain, ruleIdx)"
                                                @dragend="onDragEnd">
                                                <!-- 1. Priority Arrows & Drag Handle -->
                                                <td class="ps-3 py-2">
                                                   <div class="d-flex align-items-center gap-1">
                                                      <i class="fa-solid fa-grip-vertical text-muted cursor-grab me-1" style="font-size: 11px;"></i>
                                                      <span class="badge bg-dark-subtle text-dark fw-bold font-monospace px-2 py-1" style="font-size: 11px; border-radius: 4px; min-width: 22px; text-align: center;">{{ rule.priority }}</span>
                                                      <div class="d-flex flex-column gap-0 lh-1" style="margin-left: 2px;">
                                                         <button class="btn btn-link text-secondary p-0 border-0 shadow-none" 
                                                            style="font-size: 9px; line-height: 1; height: 10px; text-decoration: none;" 
                                                            :disabled="ruleIdx === 0"
                                                            @click.stop="moveRulePriority(domain, ruleIdx, -1)">
                                                            <i class="fa-solid fa-caret-up"></i>
                                                         </button>
                                                         <button class="btn btn-link text-secondary p-0 border-0 shadow-none" 
                                                            style="font-size: 9px; line-height: 1; height: 10px; text-decoration: none;" 
                                                            :disabled="ruleIdx === domain.rules.length - 1"
                                                            @click.stop="moveRulePriority(domain, ruleIdx, 1)">
                                                            <i class="fa-solid fa-caret-down"></i>
                                                         </button>
                                                      </div>
                                                   </div>
                                                </td>

                                                <!-- 2. Rule Exception Pattern -->
                                                <td class="py-2 font-monospace text-dark fw-bold" style="font-size: 13.5px;">
                                                   <span style="color: #00A0C1;" v-if="rule.subdomain">{{ rule.subdomain }}.</span><!--
                                                   --><span class="text-dark-50" style="font-weight: 500;">{{ domain.domain }}</span><!--
                                                   --><span style="color: #00A0C1;" v-if="rule.path">{{ rule.path.startsWith('/') ? rule.path : '/' + rule.path }}</span><!--
                                                   --><span style="color: #00A0C1;" v-if="!rule.subdomain && !rule.path">{{ rule.value }}</span>
                                                </td>

                                                <!-- 3. Subdomains Inherit Switch -->
                                                <td class="py-2">
                                                   <div class="form-check form-switch m-0" @click.stop>
                                                      <input class="form-check-input" type="checkbox" role="switch" 
                                                         :checked="rule.subdomains_inherit"
                                                         @change="toggleRuleSubdomainsInherit(domain, ruleIdx)" 
                                                         style="cursor: pointer; width: 28px; height: 14px;" />
                                                      <label class="form-check-label text-muted small ms-1" style="font-size: 11px;">Inherit</label>
                                                   </div>
                                                </td>

                                                <!-- 4. Upload Switch -->
                                                <td class="py-2">
                                                   <div class="form-check form-switch m-0" @click.stop>
                                                      <input class="form-check-input" type="checkbox" role="switch" 
                                                         :checked="rule.allow_upload"
                                                         @change="toggleRuleAllowUpload(domain, ruleIdx)" 
                                                         style="cursor: pointer; width: 28px; height: 14px;" />
                                                      <label class="form-check-label text-muted small ms-1" style="font-size: 11px;">Allow</label>
                                                   </div>
                                                </td>

                                                <!-- 5. Audit Note -->
                                                <td class="py-2" @click.stop>
                                                   <input v-model="rule.note" type="text" 
                                                      class="form-control form-control-xs font-monospace bg-light border" 
                                                      placeholder="No justification note" 
                                                      @change="saveRules(domain)"
                                                      style="font-size: 11px; height: 26px; border-radius: 4px; padding: 2px 6px; width: 100%; max-width: 220px;" />
                                                </td>

                                                <!-- 7. Action Pills -->
                                                <td class="py-2" @click.stop>
                                                   <div class="btn-group" role="group">
                                                      <button type="button"
                                                         class="btn btn-outline-left btn-xs rounded-start-pill btn-check-left hand-cursor px-2.5"
                                                         @click="updateRuleAction(domain, ruleIdx, 'allow')"
                                                         :class="{ active: rule.action === 'allow' }"
                                                         style="font-size: 9.5px; font-weight: bold; height: 25px; padding: 2px 8px;">Allow</button>
                                                      <button type="button"
                                                         class="btn btn-outline-right btn-xs rounded-end-pill btn-check-right hand-cursor px-2.5"
                                                         @click="updateRuleAction(domain, ruleIdx, 'block')"
                                                         :class="{ active: rule.action === 'block' }"
                                                         style="font-size: 9.5px; font-weight: bold; height: 25px; padding: 2px 8px;">Block</button>
                                                   </div>
                                                </td>

                                                <!-- 8. Remove Trash -->
                                                <td class="text-end pe-3 py-2">
                                                   <button class="btn btn-link text-danger p-0 border-0 shadow-none" @click.stop="deleteRule(domain, ruleIdx)" title="Delete Rule">
                                                      <i class="fa-regular fa-trash-can"></i>
                                                   </button>
                                                </td>
                                             </tr>
                                          </tbody>
                                       </table>
                                    </div>

                                     <!-- Add New Exception Form Inline -->
                                     <div class="card border-0 shadow-none p-3" style="width: 100%; border-radius: 8px; background-color: #f1f3f7;">
                                        <h6 class="mb-2 fw-bold text-secondary" style="font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">
                                           <i class="fa-solid fa-plus-minus me-1"></i> Add Exception Rule
                                        </h6>
                                        <form @submit.prevent="addRule(domain)" class="row g-2 align-items-center">
                                           <!-- 2-Field Unified Layout -->
                                           <div class="col-12 col-sm-5">
                                              <div class="d-flex align-items-center gap-2">
                                                 <input v-model="newRule.subdomain" type="text" class="form-control form-control-sm font-monospace text-end bg-white" 
                                                    placeholder="subdomain" style="font-size: 13px; width: 110px; height: 36px; border-radius: 6px; border-color: #ced4da !important;" />
                                                 <span class="text-secondary font-monospace fw-bold" style="font-size: 13px; user-select: none; white-space: nowrap;">
                                                    .{{ domain.domain }}/
                                                 </span>
                                                 <input v-model="newRule.path" type="text" class="form-control form-control-sm font-monospace bg-white" 
                                                    placeholder="path/*" style="font-size: 13px; flex: 1; height: 36px; border-radius: 6px; border-color: #ced4da !important;" />
                                              </div>
                                           </div>

                                           <div class="col-12 col-sm-4">
                                              <input v-model="newRule.note" type="text" class="form-control form-control-sm" 
                                                 placeholder="Optional Note / Justification Reason" style="height: 36px; border-radius: 6px;" />
                                           </div>
                                           <div class="col-12 col-sm-2">
                                              <select v-model="newRule.action" class="form-select form-select-sm" required style="height: 36px; border-radius: 6px;">
                                                 <option value="allow">Allow</option>
                                                 <option value="block">Block</option>
                                              </select>
                                           </div>
                                           <div class="col-12 col-sm-1">
                                              <button type="submit" class="fw-bold btn btn-info text-light w-100 d-flex align-items-center justify-content-center gap-1" style="height: 36px; border-radius: 6px;">
                                                 <i class="fa-solid fa-plus"></i> Add
                                              </button>
                                           </div>

                                           <!-- Intelligent Live Preview Helper -->
                                           <div class="col-12 mt-2" v-if="newRule.subdomain || newRule.path">
                                              <div class="d-flex align-items-center gap-2 bg-white px-2.5 py-1.5 border rounded-2 d-inline-flex shadow-sm" style="font-size: 12px; border-radius: 6px;">
                                                 <span class="badge bg-warning text-dark fw-bold" style="font-size: 10px; border-radius: 4px;">LIVE PREVIEW</span>
                                                 <span class="text-muted small">&rarr;</span>
                                                 <span class="font-monospace text-dark fw-bold">
                                                    <span style="color: #00A0C1;" v-if="newRule.subdomain">{{ newRule.subdomain.trim() }}.</span><!--
                                                    --><span class="text-dark">{{ domain.domain }}</span><!--
                                                    --><span style="color: #00A0C1;" v-if="newRule.path">{{ newRule.path.trim().startsWith('/') ? newRule.path.trim() : '/' + newRule.path.trim() }}</span>
                                                 </span>
                                              </div>
                                           </div>
                                        </form>
                                     </div>
                                 </div>
                              </td>
                           </tr>
                        </template>
                     </tbody>
                  </table>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce } from '@/plugins/helper.js'

export default {
   name: "DomainList",
   data() {
      return {
         domains: [],
         search: "",
         isLoading: false,
         expandedRowIndex: null,
         debounceFn: null,
         pagination: {
            page: 1,
            pageSize: 25,
            totalItems: 0,
            totalPages: 1
         },
         newDomain: {
            domain: "",
            default_action: "allow",
            apply_to_subdomains: true,
            note: ""
         },
         newRule: {
            subdomain: "",
            path: "",
            note: "",
            action: "allow"
         },
         draggingDomainId: null,
         draggingRuleIndex: null
      }
   },
   created() {
      this.debounceFn = debounce(() => {
         this.getDomainList()
      }, 350)
      this.getDomainList()
   },
   methods: {
      async getDomainList() {
         this.isLoading = true
         const uuid = this.$store.state.auth.organisation_uuid
         let apiPath = `dashboard/${uuid}/domain-list?page=${this.pagination.page}&page_size=${this.pagination.pageSize}`
         
         if (this.search && this.search.trim() !== "") {
            apiPath += `&search_key=${encodeURIComponent(this.search)}`
         }

         try {
            const response = await this.$axios.get(apiPath)
            if (response.data && response.data.success) {
               this.domains = (response.data.domain_list || []).map(domain => {
                  domain.note = domain.note || ""
                  if (domain.rules) {
                     domain.rules = domain.rules.map(rule => {
                        return {
                           ...rule,
                           note: rule.note || ""
                        }
                     })
                  }
                  return domain
               })
               const pag = response.data.pagination
               if (pag) {
                  this.pagination.totalItems = pag.total_count || 0
                  this.pagination.totalPages = Math.ceil((pag.total_count || 0) / this.pagination.pageSize)
               }
            } else {
               this.domains = []
            }
         } catch (error) {
            console.error("Failed to load domain rules list:", error)
            this.$toast.error("Failed to load domain rules list.")
         } finally {
            this.isLoading = false
         }
      },

      async addDomain() {
         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list`

         try {
            const payload = {
               domain: this.newDomain.domain.trim(),
               default_action: this.newDomain.default_action,
               apply_to_subdomains: this.newDomain.apply_to_subdomains ? 1 : 0,
               note: this.newDomain.note ? this.newDomain.note.trim() : ""
            }

            const response = await this.$axios.post(apiPath, payload)
            if (response.data && response.data.success) {
               this.$toast.success(response.data.message || "Domain added successfully!")
               this.newDomain.domain = ""
               this.newDomain.default_action = "allow"
               this.newDomain.apply_to_subdomains = true
               this.newDomain.note = ""
               this.getDomainList()
            }
         } catch (error) {
            console.error("Failed to register domain:", error)
            const msg = error.response && error.response.data && error.response.data.message 
               ? error.response.data.message 
               : "Failed to register domain name."
            this.$toast.error(msg)
         }
      },

      async toggleSubdomainInherit(domain) {
         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list/${domain.id}`
         const nextVal = domain.apply_to_subdomains > 0 ? 0 : 1

         try {
            const payload = {
               default_action: domain.default_action,
               apply_to_subdomains: nextVal,
               note: domain.note || ""
            }
            const response = await this.$axios.put(apiPath, payload)
            if (response.data && response.data.success) {
               domain.apply_to_subdomains = nextVal
               this.$toast.success("Domain settings updated.")
            }
         } catch (error) {
            console.error("Failed to update subdomain option:", error)
            this.$toast.error("Failed to update subdomain inherit option.")
         }
      },

      async saveDomainNote(domain) {
         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list/${domain.id}`

         try {
            const payload = {
               default_action: domain.default_action,
               apply_to_subdomains: domain.apply_to_subdomains,
               note: domain.note || ""
            }
            const response = await this.$axios.put(apiPath, payload)
            if (response.data && response.data.success) {
               this.$toast.success("Domain audit note updated successfully.")
            }
         } catch (error) {
            console.error("Failed to update domain note:", error)
            this.$toast.error("Failed to update domain note.")
         }
      },

      async deleteDomain(domain) {
         if (!confirm(`Are you absolutely sure you want to delete '${domain.domain}' and all its exceptions?`)) {
            return
         }

         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list/${domain.id}`

         try {
            const response = await this.$axios.delete(apiPath)
            if (response.data && response.data.success) {
               this.$toast.success("Domain and exceptions deleted.")
               this.expandedRowIndex = null
               this.getDomainList()
            }
         } catch (error) {
            console.error("Failed to delete domain record:", error)
            this.$toast.error("Failed to delete domain record.")
         }
      },

      async addRule(domain) {
         const subdomain = (this.newRule.subdomain || "").trim().toLowerCase()
         let path = (this.newRule.path || "").trim()
         if (path) {
            path = path.startsWith('/') ? path : '/' + path
         }
         
         let value = subdomain ? `${subdomain}.${domain.domain}` : domain.domain
         if (path) {
            value += path
         }

         const newObj = {
            rule_type: subdomain ? "subdomain" : "path",
            value: value,
            subdomain: subdomain,
            path: path,
            action: this.newRule.action,
            note: this.newRule.note ? this.newRule.note.trim() : "",
            priority: (domain.rules ? domain.rules.length : 0) + 1,
            subdomains_inherit: false,
            dlp: false,
            allow_upload: true
         }

         const updatedRules = domain.rules ? [...domain.rules] : []

         // Deduplicate exceptions on subdomain and path criteria
         const exists = updatedRules.some(r => r.subdomain === newObj.subdomain && r.path === newObj.path)
         if (exists) {
            this.$toast.warning("This exception rule already exists.")
            return
         }

         updatedRules.push(newObj)

         await this.saveRulesList(domain, updatedRules)
         this.$toast.success("Exception rule added successfully.")
         this.newRule.value = ""
         this.newRule.subdomain = ""
         this.newRule.path = ""
         this.newRule.note = ""
         this.newRule.action = "allow"
      },

      async deleteRule(domain, ruleIdx) {
         const updatedRules = domain.rules ? [...domain.rules] : []
         updatedRules.splice(ruleIdx, 1)

         // Recompute priority counts
         updatedRules.forEach((rule, idx) => {
            rule.priority = idx + 1
         })

         await this.saveRulesList(domain, updatedRules)
         this.$toast.success("Exception rule deleted.")
      },

      async updateDefaultAction(domain, action) {
         if (domain.default_action === action) return
         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list/${domain.id}`

         try {
            const payload = {
               default_action: action,
               apply_to_subdomains: domain.apply_to_subdomains,
               note: domain.note || ""
            }
            const response = await this.$axios.put(apiPath, payload)
            if (response.data && response.data.success) {
               domain.default_action = action
               this.$toast.success(`Default action updated to ${action.toUpperCase()}.`)
            }
         } catch (error) {
            console.error("Failed to update default action:", error)
            this.$toast.error("Failed to update default action.")
         }
      },

      async updateRuleAction(domain, ruleIdx, action) {
         if (domain.rules[ruleIdx].action === action) return
         const updatedRules = domain.rules ? [...domain.rules] : []
         if (updatedRules[ruleIdx]) {
            updatedRules[ruleIdx].action = action
         }
         await this.saveRulesList(domain, updatedRules)
         this.$toast.success(`Action updated to ${action.toUpperCase()}.`)
      },

      async toggleRuleSubdomainsInherit(domain, ruleIdx) {
         const updatedRules = domain.rules ? [...domain.rules] : []
         if (updatedRules[ruleIdx]) {
            updatedRules[ruleIdx].subdomains_inherit = !updatedRules[ruleIdx].subdomains_inherit
         }
         await this.saveRulesList(domain, updatedRules)
         this.$toast.success("Subdomain inheritance toggled.")
      },

      async toggleRuleDlp(domain, ruleIdx) {
         const updatedRules = domain.rules ? [...domain.rules] : []
         if (updatedRules[ruleIdx]) {
            updatedRules[ruleIdx].dlp = !updatedRules[ruleIdx].dlp
         }
         await this.saveRulesList(domain, updatedRules)
         this.$toast.success("DLP state updated.")
      },

      async toggleRuleAllowUpload(domain, ruleIdx) {
         const updatedRules = domain.rules ? [...domain.rules] : []
         if (updatedRules[ruleIdx]) {
            updatedRules[ruleIdx].allow_upload = !updatedRules[ruleIdx].allow_upload
         }
         await this.saveRulesList(domain, updatedRules)
         this.$toast.success("File upload policy updated.")
      },

      async moveRulePriority(domain, ruleIdx, direction) {
         const updatedRules = domain.rules ? [...domain.rules] : []
         const targetIdx = ruleIdx + direction
         if (targetIdx >= 0 && targetIdx < updatedRules.length) {
            // Swap positions
            const temp = updatedRules[ruleIdx]
            updatedRules[ruleIdx] = updatedRules[targetIdx]
            updatedRules[targetIdx] = temp

            // Recalculate priorities
            updatedRules.forEach((rule, idx) => {
               rule.priority = idx + 1
            })

            await this.saveRulesList(domain, updatedRules)
            this.$toast.success("Priority order updated.")
         }
      },

      async saveRulesList(domain, rules, showToastMessage = "") {
         const uuid = this.$store.state.auth.organisation_uuid
         const apiPath = `dashboard/${uuid}/domain-list/${domain.id}/rules`
         try {
            const response = await this.$axios.put(apiPath, { rules })
            if (response.data && response.data.success) {
               domain.rules = response.data.rules || []
               // Ensure notes are bound reactively
               domain.rules = domain.rules.map(rule => {
                  return {
                     ...rule,
                     note: rule.note || ""
                  }
               })
               if (showToastMessage) {
                  this.$toast.success(showToastMessage)
               }
            }
         } catch (error) {
            console.error("Failed to save rules list:", error)
            this.$toast.error("Failed to save exception rules.")
         }
      },

      saveRules(domain) {
         this.saveRulesList(domain, domain.rules, "Exception rule note updated successfully.")
      },

      onDragStart(event, domain, index) {
         this.draggingDomainId = domain.id
         this.draggingRuleIndex = index
         event.dataTransfer.effectAllowed = 'move'
      },

      onDrop(event, domain, targetIndex) {
         if (this.draggingDomainId !== domain.id) {
            this.$toast.warning("Cannot drag rules between different root domains.")
            return
         }
         if (this.draggingRuleIndex !== null && this.draggingRuleIndex !== targetIndex) {
            const updatedRules = [...domain.rules]
            const [draggedRule] = updatedRules.splice(this.draggingRuleIndex, 1)
            updatedRules.splice(targetIndex, 0, draggedRule)

            // Recompute priority counts
            updatedRules.forEach((rule, idx) => {
               rule.priority = idx + 1
            })

            this.saveRulesList(domain, updatedRules, "Priority order updated.")
         }
         this.onDragEnd()
      },

      onDragEnd() {
         this.draggingDomainId = null
         this.draggingRuleIndex = null
      },

      toggleRow(idx) {
         if (this.expandedRowIndex === idx) {
            this.expandedRowIndex = null
         } else {
            this.expandedRowIndex = idx
         }
      },

      onSearch() {
         this.pagination.page = 1
         this.debounceFn()
      },

      previousPage() {
         if (this.pagination.page > 1) {
            this.pagination.page--
            this.getDomainList()
         }
      },

      nextPage() {
         if (this.pagination.page < this.pagination.totalPages) {
            this.pagination.page++
            this.getDomainList()
         }
      },

      parsePattern(inputStr, baseDomain) {
         let cleaned = (inputStr || '').trim()
         if (!cleaned) return { subdomain: '', path: '', base: baseDomain || '' }

         if (cleaned.startsWith('http://')) {
            cleaned = cleaned.substring(7)
         } else if (cleaned.startsWith('https://')) {
            cleaned = cleaned.substring(8)
         }
         cleaned = stringsTrimSuffix(cleaned, '/')

         const parts = cleaned.split('/')
         let hostPart = parts[0].toLowerCase()
         let pathPart = parts.slice(1).join('/')

         let subdomain = ''
         let base = baseDomain || ''

         if (base) {
            if (hostPart === base) {
               subdomain = ''
            } else if (hostPart.endsWith('.' + base)) {
               subdomain = hostPart.substring(0, hostPart.length - base.length - 1)
            } else {
               if (!hostPart.includes('.')) {
                  subdomain = hostPart
               } else {
                  subdomain = hostPart.split('.')[0]
               }
            }
         } else {
            base = this.extractBaseDomain(hostPart) || hostPart
            if (hostPart !== base && hostPart.endsWith('.' + base)) {
               subdomain = hostPart.substring(0, hostPart.length - base.length - 1)
            }
         }

         let finalPath = pathPart ? '/' + pathPart : ''
         return { subdomain, path: finalPath, base }
      },

      extractBaseDomain(domain) {
         if (!domain) return ''
         const d = domain.trim().toLowerCase()
         const parts = d.split('.')
         if (parts.length <= 2) return d

         const doubleTLDs = {
            'co.uk': true, 'org.uk': true, 'me.uk': true,
            'com.au': true, 'net.au': true, 'org.au': true,
            'co.nz': true, 'com.nz': true, 'co.jp': true,
            'org.nz': true, 'or.kr': true, 'co.za': true,
            'com.sg': true, 'com.tw': true, 'com.hk': true,
            'co.id': true, 'com.my': true
         }

         const lastTwo = parts[parts.length - 2] + '.' + parts[parts.length - 1]
         if (doubleTLDs[lastTwo] && parts.length >= 3) {
            return parts[parts.length - 3] + '.' + lastTwo
         }
         return lastTwo
      }
   }
}

// Inline JS Trim helper for safety
function stringsTrimSuffix(s, suffix) {
   if (s.endsWith(suffix)) {
      return s.substring(0, s.length - suffix.length)
   }
   return s
}
</script>

<style scoped>
.custom-span-lg {
   font-size: 24px;
   line-height: 24px;
   font-weight: bold;
}
.custom-span-no-weight {
   font-size: 24px;
   line-height: 24px;
   font-weight: 250;
}
.bg-light-yellow {
   background-color: #fbf9f0 !important;
}
.fw-bold {
   font-weight: 700;
}
.font-monospace {
   font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Custom Segmented Button Group Styles for Allow/Block */
.btn-check-left { min-width: 4.5rem; border-color: #ced4da; }
.btn-check-right { min-width: 4.5rem; border-color: #ced4da; }

.btn-outline-left { color: #00a0c1; background-color: transparent; border-color: #ced4da; transition: all 0.15s ease; }
.btn-outline-left:hover, .btn-outline-left.active { color: white !important; background-color: #00a0c1 !important; border-color: #00a0c1 !important; font-weight: bold; }

.btn-outline-right { color: #f3994d; background-color: transparent; border-color: #ced4da; transition: all 0.15s ease; }
.btn-outline-right:hover, .btn-outline-right.active { color: white !important; background-color: #f3994d !important; border-color: #f3994d !important; font-weight: bold; }

.hand-cursor {
   cursor: pointer;
}
.dragging-row {
   opacity: 0.6;
   border: 2px dashed #00A0C1 !important;
   background-color: #f1f3f7 !important;
}
.cursor-grab {
   cursor: grab;
}
.cursor-grab:active {
   cursor: grabbing;
}
</style>
